/** Procedural background-pattern generators for the Background Maker tool.
 *  Every style draws directly onto a 2D canvas using only the color palette
 *  extracted from the user's photo (via aura-extract) — the photo's actual
 *  pixels are never used, only its dominant colors, so the same privacy
 *  guarantee as the Aura Scanner applies: nothing ever leaves the browser. */

/** Deterministic PRNG so a given photo + style always regenerates the same
 *  pattern (stable previews, reproducible exports) without needing to persist
 *  anything beyond a single numeric seed. */
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return function rng() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function hashSeed(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (Math.imul(31, h) + input.charCodeAt(i)) | 0;
  }
  return h;
}

function pick<T>(rng: () => number, arr: T[]): T {
  return arr[Math.floor(rng() * arr.length)] ?? arr[0];
}

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

function withAlpha(hex: string, alpha: number): string {
  const a = clamp(Math.round(alpha * 255), 0, 255).toString(16).padStart(2, "0");
  return `${hex}${a}`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace("#", "");
  return {
    r: parseInt(clean.substring(0, 2), 16) || 0,
    g: parseInt(clean.substring(2, 4), 16) || 0,
    b: parseInt(clean.substring(4, 6), 16) || 0,
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, "0")).join("")}`;
}

/** Lightens (amt > 0) or darkens (amt < 0) a hex color toward white/black. */
function shade(hex: string, amt: number): string {
  const { r, g, b } = hexToRgb(hex);
  if (amt >= 0) {
    return rgbToHex(r + (255 - r) * amt, g + (255 - g) * amt, b + (255 - b) * amt);
  }
  const p = -amt;
  return rgbToHex(r * (1 - p), g * (1 - p), b * (1 - p));
}

/** Blends two hex colors, t=0 returns a, t=1 returns b. */
function mixHex(a: string, b: string, t: number): string {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  return rgbToHex(ca.r + (cb.r - ca.r) * t, ca.g + (cb.g - ca.g) * t, ca.b + (cb.b - ca.b) * t);
}

type Point = { x: number; y: number };

function drawBlobPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, rng: () => number, points = 8) {
  const angleStep = (Math.PI * 2) / points;
  const pts: Point[] = [];
  for (let i = 0; i < points; i++) {
    const angle = i * angleStep;
    const radius = r * (0.72 + rng() * 0.56);
    pts.push({ x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius });
  }
  ctx.beginPath();
  for (let i = 0; i < points; i++) {
    const p0 = pts[i];
    const p1 = pts[(i + 1) % points];
    const mx = (p0.x + p1.x) / 2;
    const my = (p0.y + p1.y) / 2;
    if (i === 0) ctx.moveTo(mx, my);
    ctx.quadraticCurveTo(p0.x, p0.y, mx, my);
  }
  ctx.closePath();
}

/** Renders `drawInto` (full-size coordinates) onto a small offscreen canvas,
 *  blurs that layer ONCE, and composites it upscaled onto `ctx`.
 *
 *  This replaces `ctx.filter = "blur(...)"` on the main canvas, which re-runs
 *  a full-resolution blur for EVERY fill and made 3000×2000 exports take tens
 *  of seconds. Here the blur cost is bounded by the offscreen size (≤720px
 *  short side) regardless of export resolution, and the high-quality upscale
 *  adds extra softness for free. Respects the caller's globalAlpha and
 *  globalCompositeOperation for the final composite. */
function blurredLayer(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  blurPx: number,
  drawInto: (sctx: CanvasRenderingContext2D) => void,
  maxShort = 720,
  /** Optional bounding box (full-size coords) of everything drawInto paints,
   *  including the blur falloff. When given, only that region is rendered and
   *  composited — keeps small shapes from paying a full-canvas composite. */
  bounds?: { x: number; y: number; w: number; h: number },
) {
  // Clamp bounds to the canvas; bail if fully off-screen.
  let bx = 0, by = 0, bw = w, bh = h;
  if (bounds) {
    bx = clamp(bounds.x, 0, w);
    by = clamp(bounds.y, 0, h);
    bw = clamp(bounds.x + bounds.w, 0, w) - bx;
    bh = clamp(bounds.y + bounds.h, 0, h) - by;
    if (bw < 1 || bh < 1) return;
  }
  const s = Math.min(1, maxShort / Math.max(1, Math.min(w, h)));
  const sw = Math.max(1, Math.round(bw * s));
  const sh = Math.max(1, Math.round(bh * s));

  const raw = document.createElement("canvas");
  raw.width = sw;
  raw.height = sh;
  const rctx = raw.getContext("2d");
  if (!rctx) return;
  rctx.scale(s, s);
  rctx.translate(-bx, -by);
  drawInto(rctx);

  const soft = document.createElement("canvas");
  soft.width = sw;
  soft.height = sh;
  const bctx = soft.getContext("2d");
  if (!bctx) return;
  const scaledBlur = Math.max(0, blurPx * s);
  if (scaledBlur >= 0.5) bctx.filter = `blur(${Math.round(scaledBlur)}px)`;
  bctx.drawImage(raw, 0, 0);
  bctx.filter = "none";

  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(soft, 0, 0, sw, sh, bx, by, bw, bh);
  ctx.restore();
}

/** Convenience: a padded square bounding box around a circle-ish shape. */
function boxAround(cx: number, cy: number, radius: number): { x: number; y: number; w: number; h: number } {
  return { x: cx - radius, y: cy - radius, w: radius * 2, h: radius * 2 };
}

// ── Finishing-touch helpers ──────────────────────────────────────────────────
// Applied on top of every style so each export reads as a deliberately
// finished piece rather than a flat procedural fill.

/** Fine speckled grain — mimics the subtle texture of print/paper/fabric. */
function addGrain(ctx: CanvasRenderingContext2D, w: number, h: number, rng: () => number, intensity = 0.035) {
  const speckleCount = Math.round((w * h) / 850);
  for (let i = 0; i < speckleCount; i++) {
    ctx.fillStyle = withAlpha(rng() > 0.5 ? "#ffffff" : "#000000", rng() * intensity);
    ctx.fillRect(rng() * w, rng() * h, 1.4, 1.4);
  }
}

/** Soft radial darkening toward the edges — gives depth and pulls the eye
 *  toward the center of the composition, like a lens vignette. */
function addVignette(ctx: CanvasRenderingContext2D, w: number, h: number, intensity = 0.32) {
  const grad = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.32, w / 2, h / 2, Math.max(w, h) * 0.75);
  grad.addColorStop(0, "rgba(0,0,0,0)");
  grad.addColorStop(1, `rgba(0,0,0,${intensity})`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
}

/** A single soft diagonal band of light — a restrained "studio sheen" that
 *  keeps flat fills from looking inert. */
function addSheen(ctx: CanvasRenderingContext2D, w: number, h: number, rng: () => number, opacity = 0.07) {
  ctx.save();
  ctx.globalCompositeOperation = "lighten";
  const angle = 0.55 + rng() * 0.5;
  const len = Math.max(w, h) * 1.5;
  const cx = w * (0.3 + rng() * 0.4);
  const cy = h * (0.3 + rng() * 0.4);
  const x0 = cx - Math.cos(angle) * len / 2;
  const y0 = cy - Math.sin(angle) * len / 2;
  const x1 = cx + Math.cos(angle) * len / 2;
  const y1 = cy + Math.sin(angle) * len / 2;
  const grad = ctx.createLinearGradient(x0, y0, x1, y1);
  grad.addColorStop(0, "rgba(255,255,255,0)");
  grad.addColorStop(0.46, "rgba(255,255,255,0)");
  grad.addColorStop(0.5, `rgba(255,255,255,${opacity})`);
  grad.addColorStop(0.54, "rgba(255,255,255,0)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();
}

// ── Style generators ─────────────────────────────────────────────────────────

/** Evaluates a cubic bezier at parameter t. */
function cubicBez(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const u = 1 - t;
  return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3;
}

/** Draws one large fabric fold panel — the building block of Silk Flow.
 *  The fold spine is a cubic bezier crossing the full canvas; we offset it
 *  perpendicularly to build an opaque filled polygon, then shade it with a
 *  perpendicular gradient that mimics the rounded cross-section of draped
 *  satin: deep shadow in the valley, fabric base colour in the body, and a
 *  very bright near-white specular highlight at the fold crest. */
function drawSilkFold(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  rng: () => number,
  color: string,
  halfWidth: number,
  highlightSide: 1 | -1 = 1,   // which side of the spine gets the highlight
  spineOverride?: { x0: number; y0: number; cx1: number; cy1: number; cx2: number; cy2: number; x3: number; y3: number },
) {
  // Spine: cubic bezier from one canvas edge to the opposite.
  let x0: number, y0: number, x3: number, y3: number, cx1: number, cy1: number, cx2: number, cy2: number;
  if (spineOverride) {
    ({ x0, y0, cx1, cy1, cx2, cy2, x3, y3 } = spineOverride);
  } else {
    const horiz = rng() > 0.5;
    const pad = halfWidth * 0.5;
    if (horiz) {
      x0 = -pad;    y0 = h * rng();
      x3 = w + pad; y3 = h * rng();
    } else {
      x0 = w * rng(); y0 = -pad;
      x3 = w * rng(); y3 = h + pad;
    }
    cx1 = w * (0.05 + rng() * 0.90);
    cy1 = h * (0.05 + rng() * 0.90);
    cx2 = w * (0.05 + rng() * 0.90);
    cy2 = h * (0.05 + rng() * 0.90);
  }

  // ALL randomness fixed up-front (never inside the sampling loop) so the
  // outline is a smooth continuous wave, not per-sample noise.
  const waveFreq   = 1.5 + rng() * 1.5;       // width undulation frequency
  const wavePhase  = rng() * Math.PI * 2;
  const crinkleFreq  = 4 + rng() * 4;         // fine crinkle along the spine
  const crinklePhase = rng() * Math.PI * 2;
  const crinkleAmp   = halfWidth * (0.04 + rng() * 0.06);

  // Adaptive detail: small canvases (style thumbnails) need far fewer
  // gradient segments than the full preview.
  const N = Math.max(40, Math.min(140, Math.round(Math.min(w, h) / 6)));
  const spine:   Point[] = [];
  const normals: Point[] = [];
  const widths:  number[] = [];

  for (let i = 0; i <= N; i++) {
    const t  = i / N;
    let px = cubicBez(x0, cx1, cx2, x3, t);
    let py = cubicBez(y0, cy1, cy2, y3, t);

    const dt = 1 / N;
    const tx = cubicBez(x0, cx1, cx2, x3, Math.min(1, t + dt)) - cubicBez(x0, cx1, cx2, x3, Math.max(0, t - dt));
    const ty = cubicBez(y0, cy1, cy2, y3, Math.min(1, t + dt)) - cubicBez(y0, cy1, cy2, y3, Math.max(0, t - dt));
    const len = Math.sqrt(tx * tx + ty * ty) || 1;
    const nx = -ty / len;
    const ny =  tx / len;

    // Smooth crinkle displacement along the normal — fabric wrinkles.
    const crinkle = Math.sin(t * Math.PI * crinkleFreq + crinklePhase) * crinkleAmp;
    px += nx * crinkle;
    py += ny * crinkle;

    // Smooth width undulation — fabric slack.
    const wt = halfWidth * (0.80 + 0.20 * Math.sin(t * Math.PI * waveFreq + wavePhase));

    spine.push({ x: px, y: py });
    normals.push({ x: nx * highlightSide, y: ny * highlightSide });
    widths.push(wt);
  }

  // Shade segment-by-segment so the shadow→highlight gradient follows the
  // curve of the fold instead of being one flat linear ramp. Each segment is
  // a quad between consecutive spine samples, filled with its own local
  // perpendicular gradient. Segments overlap slightly to avoid hairline gaps.
  for (let i = 0; i < N; i++) {
    const j  = Math.min(N, i + 1);
    const s0 = spine[i],   s1 = spine[j];
    const n0 = normals[i], n1 = normals[j];
    const w0 = widths[i],  w1 = widths[j];

    // Quad corners: shadow edge (−normal) → highlight edge (+normal).
    const a = { x: s0.x - n0.x * w0, y: s0.y - n0.y * w0 };  // shadow, i
    const b = { x: s1.x - n1.x * w1, y: s1.y - n1.y * w1 };  // shadow, i+1
    const c = { x: s1.x + n1.x * w1, y: s1.y + n1.y * w1 };  // highlight, i+1
    const d = { x: s0.x + n0.x * w0, y: s0.y + n0.y * w0 };  // highlight, i

    // Local gradient perpendicular to this segment.
    const gx0 = (a.x + b.x) / 2, gy0 = (a.y + b.y) / 2;
    const gx1 = (c.x + d.x) / 2, gy1 = (c.y + d.y) / 2;
    const grad = ctx.createLinearGradient(gx0, gy0, gx1, gy1);
    grad.addColorStop(0.00, shade(color, -0.48));   // deep shadow valley
    grad.addColorStop(0.20, shade(color, -0.22));   // mid shadow
    grad.addColorStop(0.42, color);                  // base fabric tone
    grad.addColorStop(0.64, shade(color,  0.42));   // rising toward crest
    grad.addColorStop(0.82, shade(color,  0.60));   // bright crest highlight
    grad.addColorStop(1.00, shade(color,  0.70));   // pale peak specular

    // Extend the quad slightly along the spine direction to overlap the next
    // segment — hides antialiasing seams between segments.
    const ex = (s1.x - s0.x) * 0.6;
    const ey = (s1.y - s0.y) * 0.6;

    ctx.beginPath();
    ctx.moveTo(a.x - ex, a.y - ey);
    ctx.lineTo(b.x + ex, b.y + ey);
    ctx.lineTo(c.x + ex, c.y + ey);
    ctx.lineTo(d.x - ex, d.y - ey);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();
  }
}

/** Silk Flow — large sweeping fabric fold panels that fill the canvas with
 *  draped satin. Rendered in three passes: blurred soft glow panels that
 *  establish colour zones and blend at boundaries; crisp shaded main folds
 *  with opaque shadow-to-highlight gradients; and fine satin highlight lines
 *  at fold crests. The light background and fully-opaque panels produce the
 *  look of real draped silk lit from above rather than a dark glowing effect. */
function drawSilkFlow(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng   = mulberry32(seed);
  if (!(w > 0) || !(h > 0)) return;
  if (colors.length === 0) colors = ["#e8a7b8", "#2e7d4f", "#f2d5da"];

  // Performance: silk is nothing but soft gradients, so render at a capped
  // internal resolution and upscale — visually identical, dramatically
  // cheaper than shading thousands of gradient quads at full export size.
  const MAX_SHORT = 900;
  if (Math.min(w, h) > MAX_SHORT) {
    const scale = MAX_SHORT / Math.min(w, h);
    const ow = Math.round(w * scale), oh = Math.round(h * scale);
    const off = document.createElement("canvas");
    off.width = ow; off.height = oh;
    const octx = off.getContext("2d");
    if (octx) {
      drawSilkFlow(octx, ow, oh, colors, seed);
      ctx.save();
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(off, 0, 0, ow, oh, 0, 0, w, h);
      ctx.restore();
      return;
    }
  }
  const short = Math.min(w, h);

  // One shared flow direction — all panels drape roughly parallel, layered
  // side by side like lengths of silk laid next to each other (matches the
  // reference photos: each colour owns a region, folds don't crisscross).
  const baseAngle = (rng() > 0.5 ? 0 : Math.PI / 2) + (rng() - 0.5) * 0.6; // mostly horiz or vert, tilted
  const dx = Math.cos(baseAngle),  dy = Math.sin(baseAngle);   // along the flow
  const px = -dy,                  py = dx;                    // across the flow
  const cxm = w / 2, cym = h / 2;
  const halfDiag = Math.sqrt(w * w + h * h) / 2;
  const reach = halfDiag * 1.25;                               // spine length past edges

  // Every palette colour gets its own lane across the canvas. Shuffle order
  // so adjacent lanes vary between seeds, but all colours always appear.
  const laneColors = [...colors];
  for (let i = laneColors.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [laneColors[i], laneColors[j]] = [laneColors[j], laneColors[i]];
  }
  const K = laneColors.length;
  const span = halfDiag * 2.1;               // full extent across the flow
  const laneW = span / K;

  // Pale backdrop from a mid palette colour.
  ctx.fillStyle = shade(laneColors[0], 0.82);
  ctx.fillRect(0, 0, w, h);

  /** Builds a gently-curving spine centred on a lane offset. */
  const laneSpine = (offset: number) => {
    const wob = laneW * 0.55;                // gentle S-curve, stays in lane
    const o1 = offset + (rng() - 0.5) * wob;
    const o2 = offset + (rng() - 0.5) * wob;
    return {
      x0: cxm + px * offset - dx * reach,  y0: cym + py * offset - dy * reach,
      cx1: cxm + px * o1 - dx * reach * 0.33, cy1: cym + py * o1 - dy * reach * 0.33,
      cx2: cxm + px * o2 + dx * reach * 0.33, cy2: cym + py * o2 + dy * reach * 0.33,
      x3: cxm + px * offset + dx * reach,  y3: cym + py * offset + dy * reach,
    };
  };

  // Soft passes are rendered on a small offscreen canvas and upscaled —
  // the stretch gives the blur for free. Using ctx.filter here would run a
  // full blur for EVERY quad fill (140 per fold) and take tens of seconds.
  const makeSoftLayer = (drawInto: (sctx: CanvasRenderingContext2D) => void) => {
    const sScale = Math.min(1, 220 / short);
    const soft = document.createElement("canvas");
    soft.width  = Math.max(1, Math.round(w * sScale));
    soft.height = Math.max(1, Math.round(h * sScale));
    const sctx = soft.getContext("2d");
    if (!sctx) return null;
    sctx.scale(sScale, sScale);
    drawInto(sctx);
    return soft;
  };

  // ── Pass 1: wide soft base panel per lane — colour territory ─────────────
  const base = makeSoftLayer((sctx) => {
    for (let k = 0; k < K; k++) {
      const offset = (k + 0.5 - K / 2) * laneW;
      drawSilkFold(sctx, w, h, rng, laneColors[k], laneW * 0.85, (rng() > 0.5 ? 1 : -1), laneSpine(offset));
    }
  });
  if (base) {
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(base, 0, 0, w, h);
    ctx.restore();
  }

  // ── Pass 2: crisp shaded folds inside each lane — the visible ribbons ────
  // Two overlapping folds per lane in the lane's own colour, so each colour
  // reads as a distinct layered piece of silk with multiple drape lines.
  // No ctx.filter here: the per-segment gradients are already smooth.
  ctx.save();
  for (let k = 0; k < K; k++) {
    const laneCenter = (k + 0.5 - K / 2) * laneW;
    const folds = 2;
    for (let f = 0; f < folds; f++) {
      const offset = laneCenter + (f - (folds - 1) / 2) * laneW * 0.45 + (rng() - 0.5) * laneW * 0.15;
      const side: 1 | -1 = f % 2 === 0 ? 1 : -1;
      drawSilkFold(ctx, w, h, rng, laneColors[k], laneW * (0.38 + rng() * 0.18), side, laneSpine(offset));
    }
  }
  ctx.restore();

  // ── Pass 3: broad soft sheen along a few lanes — satin light pooling ─────
  const sheen = makeSoftLayer((sctx) => {
    for (let k = 0; k < K; k += 2) {
      const offset = (k + 0.5 - K / 2) * laneW + (rng() - 0.5) * laneW * 0.4;
      drawSilkFold(sctx, w, h, rng, shade(laneColors[k], 0.75), laneW * 0.2, (rng() > 0.5 ? 1 : -1), laneSpine(offset));
    }
  });
  if (sheen) {
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.globalCompositeOperation = "soft-light";
    ctx.drawImage(sheen, 0, 0, w, h);
    ctx.restore();
  }

  addGrain(ctx, w, h, rng, 0.012);
}

/** Faceted low-poly triangulation — a jittered grid split into triangles,
 *  each filled with a palette color and given a touch of per-facet shading
 *  so it reads as cut, lit glass rather than a flat mosaic. */
function drawLowPoly(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng = mulberry32(seed);
  const gridX = 7;
  const gridY = Math.max(4, Math.round((gridX * h) / w));
  const points: Point[][] = [];
  for (let y = 0; y <= gridY; y++) {
    const row: Point[] = [];
    for (let x = 0; x <= gridX; x++) {
      const jitter = 0.4;
      const px = (x / gridX) * w + (rng() - 0.5) * (w / gridX) * jitter;
      const py = (y / gridY) * h + (rng() - 0.5) * (h / gridY) * jitter;
      row.push({ x: clamp(px, 0, w), y: clamp(py, 0, h) });
    }
    points.push(row);
  }
  for (let y = 0; y < gridY; y++) {
    for (let x = 0; x < gridX; x++) {
      const a = points[y][x];
      const b = points[y][x + 1];
      const c = points[y + 1][x];
      const d = points[y + 1][x + 1];
      [[a, b, c], [b, d, c]].forEach((tri) => {
        ctx.beginPath();
        ctx.moveTo(tri[0].x, tri[0].y);
        ctx.lineTo(tri[1].x, tri[1].y);
        ctx.lineTo(tri[2].x, tri[2].y);
        ctx.closePath();
        const facetShade = (rng() - 0.5) * 0.3;
        ctx.fillStyle = shade(pick(rng, colors), facetShade);
        ctx.fill();
        ctx.strokeStyle = "rgba(0,0,0,0.08)";
        ctx.lineWidth = Math.max(0.5, short(w, h) * 0.0012);
        ctx.stroke();
      });
    }
  }
  addSheen(ctx, w, h, rng, 0.05);
  addVignette(ctx, w, h, 0.22);
  addGrain(ctx, w, h, rng, 0.02);
}

function short(w: number, h: number) {
  return Math.min(w, h);
}

/** Smooth multi-stop diagonal gradient sweeping through the palette, lifted
 *  with a soft corner bloom and a single sheen band — the simplest style,
 *  still the most versatile for text overlays, now with real dimension. */
function drawGradientFlow(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng = mulberry32(seed);
  const stops = [...colors].sort(() => rng() - 0.5).slice(0, Math.min(4, Math.max(2, colors.length)));
  const grad = ctx.createLinearGradient(0, 0, w * 0.18, h);
  stops.forEach((c, i) => grad.addColorStop(i / Math.max(1, stops.length - 1), c));
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // Soft bloom of light in one corner for depth.
  ctx.save();
  ctx.globalCompositeOperation = "lighten";
  const bx = rng() > 0.5 ? w * 0.08 : w * 0.92;
  const by = rng() > 0.5 ? h * 0.08 : h * 0.92;
  const bloom = ctx.createRadialGradient(bx, by, 0, bx, by, Math.min(w, h) * 0.55);
  bloom.addColorStop(0, withAlpha(shade(stops[0], 0.4), 0.35));
  bloom.addColorStop(1, withAlpha(stops[0], 0));
  ctx.fillStyle = bloom;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();

  addSheen(ctx, w, h, rng, 0.08);
  addGrain(ctx, w, h, rng, 0.018);
}

/** Soft, blurred overlapping color orbs over a two-tone neutral base plus a
 *  scattering of tiny glassy highlights — a hazy "frosted glass" wash with
 *  a bit of sparkle. */
function drawFrostedGlow(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng = mulberry32(seed);
  const baseGrad = ctx.createLinearGradient(0, 0, w, h);
  baseGrad.addColorStop(0, "#FBF6ED");
  baseGrad.addColorStop(1, "#EFE7D8");
  ctx.fillStyle = baseGrad;
  ctx.fillRect(0, 0, w, h);

  const frostBlur = Math.min(w, h) * 0.09;
  colors.forEach((c) => {
    const r = Math.min(w, h) * (0.35 + rng() * 0.3);
    const x = rng() * w;
    const y = rng() * h;
    blurredLayer(ctx, w, h, frostBlur, (sctx) => {
      const grad = sctx.createRadialGradient(x, y, 0, x, y, r);
      grad.addColorStop(0, withAlpha(c, 0.85));
      grad.addColorStop(1, withAlpha(c, 0));
      sctx.fillStyle = grad;
      sctx.beginPath();
      sctx.arc(x, y, r, 0, Math.PI * 2);
      sctx.fill();
    });
  });

  // Tiny glassy specular highlights scattered across the glow.
  ctx.save();
  ctx.globalCompositeOperation = "screen";
  for (let i = 0; i < 10; i++) {
    const x = rng() * w;
    const y = rng() * h;
    const r = Math.min(w, h) * (0.006 + rng() * 0.01);
    const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, "rgba(255,255,255,0.9)");
    grad.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  addVignette(ctx, w, h, 0.14);
  addGrain(ctx, w, h, rng, 0.02);
}

/** Layered rounded amoeba shapes, each with a soft drop-shadow and a small
 *  inner highlight so the composition reads as glossy, embossed blobs
 *  rather than flat cut-outs. */
function drawOrganicBlobs(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng = mulberry32(seed);
  ctx.fillStyle = shade(colors[0] ?? "#CCCCCC", -0.15);
  ctx.fillRect(0, 0, w, h);
  const blobCount = 6;
  const short_ = Math.min(w, h);
  for (let i = 0; i < blobCount; i++) {
    const color = pick(rng, colors);
    const cx = rng() * w;
    const cy = rng() * h;
    const baseR = short_ * (0.28 + rng() * 0.3);

    // Soft drop shadow offset toward the light source for a lifted feel.
    ctx.save();
    ctx.globalCompositeOperation = "multiply";
    blurredLayer(ctx, w, h, short_ * 0.02, (sctx) => {
      drawBlobPath(sctx, cx + baseR * 0.06, cy + baseR * 0.08, baseR, rng);
      sctx.fillStyle = "rgba(0,0,0,0.18)";
      sctx.fill();
    }, 720, boxAround(cx + baseR * 0.06, cy + baseR * 0.08, baseR * 1.4 + short_ * 0.06));
    ctx.restore();

    drawBlobPath(ctx, cx, cy, baseR, rng);
    ctx.fillStyle = withAlpha(color, 0.94);
    ctx.fill();

    // Small inner highlight, offset up-left, for a glossy sheen.
    ctx.save();
    ctx.globalCompositeOperation = "screen";
    blurredLayer(ctx, w, h, short_ * 0.02, (sctx) => {
      drawBlobPath(sctx, cx - baseR * 0.22, cy - baseR * 0.24, baseR * 0.42, rng, 6);
      sctx.fillStyle = withAlpha(shade(color, 0.55), 0.4);
      sctx.fill();
    }, 720, boxAround(cx - baseR * 0.22, cy - baseR * 0.24, baseR * 0.6 + short_ * 0.06));
    ctx.restore();
  }
  addVignette(ctx, w, h, 0.24);
  addGrain(ctx, w, h, rng, 0.018);
}

/** Diffuse pastel wash with layered, multiply-blended pigment passes and a
 *  fine paper grain — mimics watercolor bleeding and re-wetting on paper
 *  rather than a single flat blur. */
function drawWatercolor(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng = mulberry32(seed);
  ctx.fillStyle = "#FBF9F5";
  ctx.fillRect(0, 0, w, h);

  // First wash — broad, very soft base color zones.
  const wash1Blur = Math.min(w, h) * 0.14;
  colors.forEach((c) => {
    const r = Math.min(w, h) * (0.45 + rng() * 0.35);
    const x = rng() * w;
    const y = rng() * h;
    blurredLayer(ctx, w, h, wash1Blur, (sctx) => {
      sctx.fillStyle = withAlpha(c, 0.38);
      sctx.beginPath();
      sctx.arc(x, y, r, 0, Math.PI * 2);
      sctx.fill();
    });
  });

  // Second wash — smaller, multiply-blended pigment pooling for depth where
  // colors overlap, the way re-wet watercolor darkens at its edges.
  ctx.save();
  ctx.globalCompositeOperation = "multiply";
  const wash2Blur = Math.min(w, h) * 0.07;
  colors.forEach((c) => {
    const r = Math.min(w, h) * (0.16 + rng() * 0.22);
    const x = rng() * w;
    const y = rng() * h;
    blurredLayer(ctx, w, h, wash2Blur, (sctx) => {
      sctx.fillStyle = withAlpha(c, 0.22);
      sctx.beginPath();
      sctx.arc(x, y, r, 0, Math.PI * 2);
      sctx.fill();
    });
  });
  ctx.restore();

  addGrain(ctx, w, h, rng, 0.03);
  addVignette(ctx, w, h, 0.12);
}

/** Scattered paint drops and drips of varying size and shape over a bold
 *  base color, with glossy highlight arcs on the larger drops — energetic
 *  and tactile rather than a flat scatter of dots. */
function drawSplatter(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng = mulberry32(seed);
  ctx.fillStyle = pick(rng, colors);
  ctx.fillRect(0, 0, w, h);
  const dropCount = Math.round((w * h) / 7000);
  for (let i = 0; i < dropCount; i++) {
    const color = pick(rng, colors);
    const x = rng() * w;
    const y = rng() * h;
    const r = rng() * rng() * Math.min(w, h) * 0.07 + 3;
    ctx.globalAlpha = 0.55 + rng() * 0.4;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();

    // Occasionally drag a tapering drip tail off the drop.
    if (rng() > 0.72) {
      const tailLen = r * (1.5 + rng() * 3);
      const tailAngle = rng() * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(x - Math.cos(tailAngle) * r * 0.5, y - Math.sin(tailAngle) * r * 0.5);
      ctx.lineTo(x + Math.cos(tailAngle) * r * 0.5, y + Math.sin(tailAngle) * r * 0.5);
      ctx.lineTo(x + Math.cos(tailAngle) * tailLen, y + Math.sin(tailAngle) * tailLen);
      ctx.closePath();
      ctx.fill();
    }

    // Glossy highlight arc on the larger drops.
    if (r > Math.min(w, h) * 0.025) {
      ctx.globalAlpha = 0.35;
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = Math.max(1, r * 0.12);
      ctx.beginPath();
      ctx.arc(x - r * 0.28, y - r * 0.28, r * 0.4, Math.PI * 1.1, Math.PI * 1.7);
      ctx.stroke();
    }
  }
  ctx.globalAlpha = 1;
  addGrain(ctx, w, h, rng, 0.02);
  addVignette(ctx, w, h, 0.2);
}

// ── New style generators (batch 2) ──────────────────────────────────────────

/** Relative luminance of a hex color, used to pick the darkest/lightest of
 *  the palette for high-contrast two-tone work (duotone). */
function luminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function pickContrastPair(colors: string[]): [string, string] {
  const sorted = [...colors].sort((a, b) => luminance(a) - luminance(b));
  return [sorted[0], sorted[sorted.length - 1]];
}

function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/** Mesh Gradient — many large, heavily blurred color blobs scattered across
 *  a jittered grid so they melt into one continuous field of color spanning
 *  the whole canvas, the way modern "mesh gradient" generators blend
 *  several color points together. Unlike Gradient Flow (one directional
 *  sweep) this has no single axis — color drifts in every direction. */
function drawMeshGradient(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng = mulberry32(seed);
  const short_ = short(w, h);
  ctx.fillStyle = shade(pick(rng, colors), -0.1);
  ctx.fillRect(0, 0, w, h);

  const meshBlur = short_ * 0.16;
  const cols = 3;
  const rows = 3;
  for (let gy = 0; gy < rows; gy++) {
    for (let gx = 0; gx < cols; gx++) {
      const x = ((gx + 0.5) / cols) * w + (rng() - 0.5) * (w / cols) * 0.7;
      const y = ((gy + 0.5) / rows) * h + (rng() - 0.5) * (h / rows) * 0.7;
      const r = short_ * (0.32 + rng() * 0.22);
      const color = pick(rng, colors);
      blurredLayer(ctx, w, h, meshBlur, (sctx) => {
        const grad = sctx.createRadialGradient(x, y, 0, x, y, r);
        grad.addColorStop(0, withAlpha(shade(color, 0.08), 0.95));
        grad.addColorStop(1, withAlpha(color, 0));
        sctx.fillStyle = grad;
        sctx.beginPath();
        sctx.arc(x, y, r, 0, Math.PI * 2);
        sctx.fill();
      });
    }
  }

  addSheen(ctx, w, h, rng, 0.08);
  addVignette(ctx, w, h, 0.16);
  addGrain(ctx, w, h, rng, 0.02);
}

/** Grainy Gradient — a calm, matte 3-stop radial blend with heavy monochrome
 *  film grain as the dominant texture rather than an accent. No sheen, no
 *  bloom — the flattest, most minimal style in the set on purpose. */
function drawGrainyGradient(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng = mulberry32(seed);
  const stops = [...colors].sort(() => rng() - 0.5).slice(0, Math.min(3, Math.max(2, colors.length)));
  const cx = w * (0.3 + rng() * 0.4);
  const cy = h * (0.3 + rng() * 0.4);
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) * 0.85);
  stops.forEach((c, i) => grad.addColorStop(i / Math.max(1, stops.length - 1), shade(c, 0.12)));
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  addGrain(ctx, w, h, rng, 0.09);
  addVignette(ctx, w, h, 0.1);
}

/** Liquid Metaball — several soft circles are blurred and then pushed back
 *  into hard contrast so their edges "fuse" where they overlap, the classic
 *  metaball trick. The fused mask is used to cut a colorful gradient out of
 *  a darker backdrop, giving a molten, merging-blob look distinct from the
 *  separately-outlined Organic Blobs style. */
function drawLiquidMetaball(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng = mulberry32(seed);
  const short_ = short(w, h);

  const backdrop = ctx.createLinearGradient(0, 0, w, h);
  backdrop.addColorStop(0, shade(pick(rng, colors), -0.55));
  backdrop.addColorStop(1, shade(pick(rng, colors), -0.7));
  ctx.fillStyle = backdrop;
  ctx.fillRect(0, 0, w, h);

  // The blur+contrast fuse trick only needs enough resolution for smooth
  // blob edges — run it at a capped size and upscale the mask, so the
  // full-resolution blur never runs at export size.
  const ms = Math.min(1, 900 / short_);
  const mw = Math.max(1, Math.round(w * ms));
  const mh = Math.max(1, Math.round(h * ms));

  const raw = document.createElement("canvas");
  raw.width = mw;
  raw.height = mh;
  const rawCtx = raw.getContext("2d");
  const blobCount = 6 + Math.floor(rng() * 4);
  if (rawCtx) {
    rawCtx.scale(ms, ms);
    rawCtx.fillStyle = "#ffffff";
    for (let i = 0; i < blobCount; i++) {
      const r = short_ * (0.11 + rng() * 0.13);
      const x = w * (0.12 + rng() * 0.76);
      const y = h * (0.12 + rng() * 0.76);
      rawCtx.beginPath();
      rawCtx.arc(x, y, r, 0, Math.PI * 2);
      rawCtx.fill();
    }
  }

  const fused = document.createElement("canvas");
  fused.width = mw;
  fused.height = mh;
  const fusedCtx = fused.getContext("2d");
  if (fusedCtx) {
    fusedCtx.filter = `blur(${Math.round(short_ * 0.032 * ms)}px) contrast(32)`;
    fusedCtx.drawImage(raw, 0, 0);
    fusedCtx.filter = "none";
  }

  const blobLayer = document.createElement("canvas");
  blobLayer.width = w;
  blobLayer.height = h;
  const blobCtx = blobLayer.getContext("2d");
  if (blobCtx) {
    const colorGrad = blobCtx.createLinearGradient(0, 0, w, h);
    colors.forEach((c, i) => colorGrad.addColorStop(i / Math.max(1, colors.length - 1), shade(c, 0.15)));
    blobCtx.fillStyle = colorGrad;
    blobCtx.fillRect(0, 0, w, h);
    blobCtx.globalCompositeOperation = "destination-in";
    blobCtx.imageSmoothingEnabled = true;
    blobCtx.imageSmoothingQuality = "high";
    blobCtx.drawImage(fused, 0, 0, mw, mh, 0, 0, w, h);
    blobCtx.globalCompositeOperation = "source-over";
  }
  ctx.drawImage(blobLayer, 0, 0);

  addSheen(ctx, w, h, rng, 0.1);
  addVignette(ctx, w, h, 0.24);
  addGrain(ctx, w, h, rng, 0.018);
}

/** Holographic Chrome — a tight, repeating rainbow-shift gradient through
 *  the palette (like light diffracting off foil), overlaid with fine
 *  diagonal foil striping and a bright specular streak. High-shine and
 *  metallic rather than soft or matte. */
function drawHolographic(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng = mulberry32(seed);
  const angle = rng() * Math.PI * 2;
  const len = Math.max(w, h) * 1.3;
  const cx = w / 2;
  const cy = h / 2;
  const x0 = cx - Math.cos(angle) * len / 2;
  const y0 = cy - Math.sin(angle) * len / 2;
  const x1 = cx + Math.cos(angle) * len / 2;
  const y1 = cy + Math.sin(angle) * len / 2;
  const grad = ctx.createLinearGradient(x0, y0, x1, y1);
  const repeats = 3;
  const cycle = ensurePaletteSize(colors, 4);
  const totalStops = cycle.length * repeats;
  for (let i = 0; i <= totalStops; i++) {
    const c = cycle[i % cycle.length];
    grad.addColorStop(clamp(i / totalStops, 0, 1), shade(c, 0.05));
  }
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // Fine diagonal foil striping.
  ctx.save();
  ctx.globalCompositeOperation = "overlay";
  const stripeAngle = angle + Math.PI / 2;
  ctx.translate(cx, cy);
  ctx.rotate(stripeAngle);
  const span = Math.max(w, h) * 1.6;
  const stripeGap = Math.max(3, short(w, h) * 0.012);
  for (let x = -span; x < span; x += stripeGap) {
    ctx.fillStyle = `rgba(255,255,255,${(x / stripeGap) % 2 === 0 ? 0.05 : 0})`;
    ctx.fillRect(x, -span, stripeGap / 2, span * 2);
  }
  ctx.restore();

  addSheen(ctx, w, h, rng, 0.16);
  addSheen(ctx, w, h, rng, 0.1);
  addGrain(ctx, w, h, rng, 0.015);
}

/** Aurora Glow — wide, softly wavering ribbons of translucent color drifting
 *  over a near-black night sky, light-blended so they glow where they cross,
 *  plus a scatter of tiny stars. Distinct from Frosted Glow's compact round
 *  orbs: aurora reads as sweeping horizontal bands, not blobs. */
function drawAuroraGlow(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng = mulberry32(seed);
  const sky = ctx.createLinearGradient(0, 0, 0, h);
  sky.addColorStop(0, "#03040c");
  sky.addColorStop(1, "#0a0d1c");
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, h);

  for (let i = 0; i < 60; i++) {
    ctx.fillStyle = withAlpha("#ffffff", rng() * 0.5);
    ctx.fillRect(rng() * w, rng() * h * 0.7, 1, 1);
  }

  ctx.save();
  ctx.globalCompositeOperation = "lighten";
  const auroraBlur = short(w, h) * 0.045;
  const bandCount = 4;
  for (let i = 0; i < bandCount; i++) {
    const color = pick(rng, colors);
    const baseY = h * (0.15 + (i / bandCount) * 0.6);
    const amp = h * (0.06 + rng() * 0.08);
    blurredLayer(ctx, w, h, auroraBlur, (sctx) => {
      sctx.beginPath();
      sctx.moveTo(-w * 0.1, baseY);
      const segments = 6;
      for (let s = 0; s <= segments; s++) {
        const x = -w * 0.1 + (w * 1.2 * s) / segments;
        const y = baseY + Math.sin(s * 1.3 + rng() * 2) * amp;
        sctx.lineTo(x, y);
      }
      for (let s = segments; s >= 0; s--) {
        const x = -w * 0.1 + (w * 1.2 * s) / segments;
        const y = baseY + amp * 1.8 + Math.sin(s * 1.3 + rng() * 2) * amp;
        sctx.lineTo(x, y);
      }
      sctx.closePath();
      const bandGrad = sctx.createLinearGradient(0, baseY - amp, 0, baseY + amp * 2.8);
      bandGrad.addColorStop(0, withAlpha(color, 0));
      bandGrad.addColorStop(0.5, withAlpha(shade(color, 0.15), 0.55));
      bandGrad.addColorStop(1, withAlpha(color, 0));
      sctx.fillStyle = bandGrad;
      sctx.fill();
    });
  }
  ctx.restore();

  addVignette(ctx, w, h, 0.38);
  addGrain(ctx, w, h, rng, 0.02);
}

/** Duotone — true luminance-mapped duotone in the style of Spotify's iconic
 *  covers. A procedural luminance source (radial falloff + organic swirl
 *  distortion + sine-wave noise) is remapped pixel-by-pixel through a
 *  three-stop gradient built from the palette's darkest, midtone, and
 *  lightest colors. The pixel computation runs on a downsampled offscreen
 *  canvas (capped at 480px short-side) then scaled back up so large canvases
 *  stay fast. Finished with CRT scanlines, a subtle chromatic aberration
 *  shift, a center glow halo, and a deep vignette. */
function drawDuotone(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng = mulberry32(seed);
  const palette = ensurePaletteSize(colors, 3);
  const [dark, light] = pickContrastPair(palette);
  const mid = palette.find(c => c !== dark && c !== light) ?? mixHex(dark, light, 0.5);

  const c1 = hexToRgb(shade(dark, -0.15));
  const c2 = hexToRgb(mid);
  const c3 = hexToRgb(shade(light, 0.18));

  // All pattern parameters live in normalized [0…1] space so the same seed
  // produces an identical pattern at any resolution — thumbnail and full export match.
  const nfx = 0.42 + rng() * 0.16;   // focal point, normalized
  const nfy = 0.42 + rng() * 0.16;
  const maxDist = Math.sqrt(nfx * nfx + nfy * nfy);

  const swirlStrength = 0.22 + rng() * 0.22;
  const swirlFreq    = 2 + Math.floor(rng() * 3);
  const noiseA       = 0.12 + rng() * 0.12;
  const noiseB       = 0.15 + rng() * 0.18;

  // ── Pixel-level luminance remap on a downsampled offscreen canvas ─────────
  // Compute at capped resolution (≤480px short side) for speed, scale up after.
  // Because coordinates are normalized the pattern is identical at all sizes.
  const MAX_PX = 480;
  const sf = Math.min(1, MAX_PX / Math.min(w, h));
  const pw = Math.max(1, Math.round(w * sf));
  const ph = Math.max(1, Math.round(h * sf));

  const offscreen = document.createElement("canvas");
  offscreen.width = pw;
  offscreen.height = ph;
  const octx = offscreen.getContext("2d")!;
  const imgData = octx.createImageData(pw, ph);
  const data = imgData.data;

  let idx = 0;
  for (let y = 0; y < ph; y++) {
    const ny = y / ph;              // normalized y in [0…1]
    const dy = ny - nfy;
    for (let x = 0; x < pw; x++) {
      const nx = x / pw;            // normalized x in [0…1]
      const dx = nx - nfx;
      const dist = Math.sqrt(dx * dx + dy * dy);   // normalized distance
      const baseL = 1 - dist / maxDist;
      const angle = Math.atan2(dy, dx) + dist * 4.5;   // 4.5 rad/unit → same visual density at any size
      const swirl = Math.sin(angle * swirlFreq) * swirlStrength;
      // Noise frequencies expressed as cycles-per-image, resolution-independent
      const noise = Math.sin(nx * 20) * Math.sin(ny * 20) * noiseA
                  + Math.cos(nx * 10 + ny * 7.5) * noiseB;
      const L = Math.max(0, Math.min(1, baseL + swirl + noise));

      let r, g, b;
      if (L < 0.5) {
        const t = L * 2;
        r = c1.r + (c2.r - c1.r) * t;
        g = c1.g + (c2.g - c1.g) * t;
        b = c1.b + (c2.b - c1.b) * t;
      } else {
        const t = (L - 0.5) * 2;
        r = c2.r + (c3.r - c2.r) * t;
        g = c2.g + (c3.g - c2.g) * t;
        b = c2.b + (c3.b - c2.b) * t;
      }
      data[idx++] = r;
      data[idx++] = g;
      data[idx++] = b;
      data[idx++] = 255;
    }
  }
  octx.putImageData(imgData, 0, 0);

  // Scale up to main canvas with smooth interpolation
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(offscreen, 0, 0, w, h);
  ctx.restore();

  // ── CRT scanlines ─────────────────────────────────────────────────────────
  ctx.fillStyle = "rgba(0,0,0,0.11)";
  for (let sy = 0; sy < h; sy += 3) {
    ctx.fillRect(0, sy, w, 1);
  }

  // ── Chromatic aberration: red-channel ghost shifted right ─────────────────
  ctx.save();
  const tmp = document.createElement("canvas");
  tmp.width = w;
  tmp.height = h;
  const tmpCtx = tmp.getContext("2d")!;
  tmpCtx.drawImage(ctx.canvas, 0, 0);
  tmpCtx.globalCompositeOperation = "multiply";
  tmpCtx.fillStyle = "#ff0000";
  tmpCtx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = "screen";
  ctx.globalAlpha = 0.1;
  ctx.drawImage(tmp, 2, 0);
  ctx.restore();

  // ── Center glow halo (pixel coords derived from normalized focal point) ────
  ctx.save();
  const { r: mr, g: mg, b: mb } = hexToRgb(mid);
  const glowGrad = ctx.createRadialGradient(
    nfx * w, nfy * h, 0,
    nfx * w, nfy * h, Math.min(w, h) * 0.28,
  );
  glowGrad.addColorStop(0, `rgba(${mr},${mg},${mb},0.42)`);
  glowGrad.addColorStop(1, `rgba(${mr},${mg},${mb},0)`);
  ctx.globalCompositeOperation = "screen";
  ctx.fillStyle = glowGrad;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();

  // ── Deep vignette ─────────────────────────────────────────────────────────
  addVignette(ctx, w, h, 0.55);
}

/** Halftone Dots — a classic print-style dot grid whose dot size scales
 *  with distance from a bright focal point, cycling through the palette
 *  cell by cell over a near-black base. The only style built from a grid
 *  of small repeating marks rather than large color fields. */
function drawHalftone(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng = mulberry32(seed);
  ctx.fillStyle = "#0b0b10";
  ctx.fillRect(0, 0, w, h);

  const cell = Math.max(7, Math.round(short(w, h) * 0.022));
  const cx = w * (0.3 + rng() * 0.4);
  const cy = h * (0.3 + rng() * 0.4);
  const maxDist = Math.hypot(w, h) * 0.55;
  let col = 0;
  for (let y = cell / 2; y < h; y += cell) {
    let row = 0;
    for (let x = cell / 2; x < w; x += cell) {
      const dist = Math.hypot(x - cx, y - cy);
      const t = clamp(1 - dist / maxDist, 0, 1);
      const jitter = 0.85 + rng() * 0.3;
      const r = cell * 0.5 * 0.12 + cell * 0.5 * 0.85 * t * jitter;
      const color = colors[(col + row) % colors.length];
      ctx.beginPath();
      ctx.arc(x, y, Math.max(0.4, r), 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.45 + 0.55 * t;
      ctx.fill();
      row++;
    }
    col++;
  }
  ctx.globalAlpha = 1;

  addVignette(ctx, w, h, 0.36);
  addGrain(ctx, w, h, rng, 0.015);
}

/** A single rounded, semi-transparent "glass" panel with a bright top-edge
 *  highlight and a soft border — the building block of Glassmorphism. */
function drawGlassPanel(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  pw: number,
  ph: number,
  radius: number,
  rotation: number,
) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);

  // Soft drop shadow beneath the panel. The layer canvas has no transform,
  // so re-apply the panel's translate/rotate inside the callback.
  ctx.save();
  ctx.globalCompositeOperation = "multiply";
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  blurredLayer(ctx, ctx.canvas.width, ctx.canvas.height, Math.min(pw, ph) * 0.12, (sctx) => {
    sctx.translate(x, y);
    sctx.rotate(rotation);
    roundRectPath(sctx, -pw / 2 + pw * 0.04, -ph / 2 + ph * 0.06, pw, ph, radius);
    sctx.fillStyle = "rgba(0,0,0,0.22)";
    sctx.fill();
  }, 720, boxAround(x, y, Math.hypot(pw, ph) * 0.7 + Math.min(pw, ph) * 0.3));
  ctx.restore();

  roundRectPath(ctx, -pw / 2, -ph / 2, pw, ph, radius);
  ctx.fillStyle = "rgba(255,255,255,0.14)";
  ctx.fill();
  ctx.lineWidth = Math.max(1, Math.min(pw, ph) * 0.008);
  ctx.strokeStyle = "rgba(255,255,255,0.4)";
  ctx.stroke();

  ctx.save();
  roundRectPath(ctx, -pw / 2, -ph / 2, pw, ph, radius);
  ctx.clip();
  const hi = ctx.createLinearGradient(0, -ph / 2, 0, 0);
  hi.addColorStop(0, "rgba(255,255,255,0.35)");
  hi.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = hi;
  ctx.fillRect(-pw / 2, -ph / 2, pw, ph / 2);
  ctx.restore();

  ctx.restore();
}

/** Glassmorphism — a hazy, colorful blurred glow behind several overlapping
 *  frosted glass panels, each with its own edge highlight and shadow. The
 *  only style built as distinct floating UI-panel shapes rather than a
 *  single continuous field. */
function drawGlassmorphism(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng = mulberry32(seed);
  ctx.fillStyle = shade(pick(rng, colors), -0.35);
  ctx.fillRect(0, 0, w, h);

  const glowBlur = short(w, h) * 0.13;
  colors.forEach((c) => {
    const r = short(w, h) * (0.3 + rng() * 0.28);
    const x = rng() * w;
    const y = rng() * h;
    blurredLayer(ctx, w, h, glowBlur, (sctx) => {
      const grad = sctx.createRadialGradient(x, y, 0, x, y, r);
      grad.addColorStop(0, withAlpha(shade(c, 0.1), 0.9));
      grad.addColorStop(1, withAlpha(c, 0));
      sctx.fillStyle = grad;
      sctx.beginPath();
      sctx.arc(x, y, r, 0, Math.PI * 2);
      sctx.fill();
    });
  });

  const panelCount = 4;
  for (let i = 0; i < panelCount; i++) {
    const pw = short(w, h) * (0.28 + rng() * 0.22);
    const ph = pw * (0.65 + rng() * 0.35);
    const x = w * (0.18 + rng() * 0.64);
    const y = h * (0.18 + rng() * 0.64);
    const rotation = (rng() - 0.5) * 0.35;
    drawGlassPanel(ctx, x, y, pw, ph, pw * 0.1, rotation);
  }

  addVignette(ctx, w, h, 0.24);
  addGrain(ctx, w, h, rng, 0.015);
}

/** A short jagged zigzag stroke, used as one of Memphis Pop's scattered
 *  flat shapes. */
function drawZigzagShape(ctx: CanvasRenderingContext2D, halfW: number, halfH: number, color: string, lineWidth: number) {
  ctx.beginPath();
  ctx.moveTo(-halfW, -halfH);
  ctx.lineTo(-halfW * 0.3, halfH);
  ctx.lineTo(halfW * 0.3, -halfH);
  ctx.lineTo(halfW, halfH);
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.stroke();
}

/** Memphis Pop — a scatter of flat, bright geometric shapes (circles,
 *  triangles, zigzags, crosses, dashed rings) at random rotations over a
 *  pale tinted base, 80s-postmodern style. The only style with no
 *  gradients or blur at all — everything is a flat, outlined vector shape. */
function drawMemphisPop(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng = mulberry32(seed);
  ctx.fillStyle = shade(pick(rng, colors), 0.82);
  ctx.fillRect(0, 0, w, h);

  const short_ = short(w, h);
  const shapeCount = Math.max(14, Math.round((w * h) / (short_ * short_ * 0.02)));
  for (let i = 0; i < shapeCount; i++) {
    const color = pick(rng, colors);
    const x = rng() * w;
    const y = rng() * h;
    const size = short_ * (0.035 + rng() * 0.1);
    const kind = Math.floor(rng() * 5);
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rng() * Math.PI * 2);
    ctx.fillStyle = color;
    ctx.strokeStyle = "rgba(24,20,30,0.85)";
    ctx.lineWidth = Math.max(1.2, size * 0.07);
    if (kind === 0) {
      ctx.beginPath();
      ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
      if (rng() > 0.5) ctx.stroke();
      else ctx.fill();
    } else if (kind === 1) {
      ctx.beginPath();
      ctx.moveTo(0, -size / 2);
      ctx.lineTo(size / 2, size / 2);
      ctx.lineTo(-size / 2, size / 2);
      ctx.closePath();
      ctx.fill();
    } else if (kind === 2) {
      drawZigzagShape(ctx, size / 2, size / 2, color, Math.max(2, size * 0.14));
    } else if (kind === 3) {
      ctx.fillRect(-size / 2, -size * 0.12, size, size * 0.24);
      ctx.fillRect(-size * 0.12, -size / 2, size * 0.24, size);
    } else {
      ctx.setLineDash([size * 0.14, size * 0.1]);
      ctx.beginPath();
      ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.restore();
  }

  addGrain(ctx, w, h, rng, 0.02);
  addVignette(ctx, w, h, 0.1);
}

/** Synthwave Grid — a retro sunset sky over a glowing horizon-line sun with
 *  cut horizontal bands, above a neon perspective grid receding to a
 *  vanishing point, plus a scatter of stars. The only style with an actual
 *  scene/horizon composition rather than an abstract field. */
function drawSynthwave(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng = mulberry32(seed);
  const short_ = short(w, h);
  const horizonY = h * 0.58;
  const palette = ensurePaletteSize(colors, 3);
  const sunColor = pick(rng, palette);
  const groundColor = pick(rng, palette);

  const sky = ctx.createLinearGradient(0, 0, 0, horizonY);
  sky.addColorStop(0, shade(pick(rng, palette), -0.6));
  sky.addColorStop(1, shade(sunColor, 0.1));
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, horizonY);

  const ground = ctx.createLinearGradient(0, horizonY, 0, h);
  ground.addColorStop(0, shade(groundColor, -0.35));
  ground.addColorStop(1, "#050308");
  ctx.fillStyle = ground;
  ctx.fillRect(0, horizonY, w, h - horizonY);

  for (let i = 0; i < 45; i++) {
    ctx.fillStyle = withAlpha("#ffffff", rng() * 0.7);
    ctx.fillRect(rng() * w, rng() * horizonY * 0.85, 1.2, 1.2);
  }

  const sunR = short_ * 0.22;
  const sunX = w * 0.5;
  const sunY = horizonY - sunR * 0.1;
  ctx.save();
  ctx.globalCompositeOperation = "lighten";
  const glow = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, sunR * 1.6);
  glow.addColorStop(0, withAlpha(shade(sunColor, 0.3), 0.85));
  glow.addColorStop(1, withAlpha(sunColor, 0));
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();

  ctx.save();
  ctx.beginPath();
  ctx.arc(sunX, sunY, sunR, 0, Math.PI * 2);
  ctx.clip();
  ctx.fillStyle = sunColor;
  ctx.fillRect(sunX - sunR, sunY - sunR, sunR * 2, sunR * 2);
  const bandColor = shade(groundColor, -0.3);
  for (let i = 0; i < 5; i++) {
    const by = sunY + sunR * 0.1 + i * sunR * 0.17;
    ctx.fillStyle = withAlpha(bandColor, 0.92);
    ctx.fillRect(sunX - sunR, by, sunR * 2, sunR * 0.06);
  }
  ctx.restore();

  ctx.save();
  ctx.strokeStyle = withAlpha(shade(sunColor, 0.35), 0.85);
  ctx.lineWidth = Math.max(1, short_ * 0.0028);
  ctx.shadowColor = sunColor;
  ctx.shadowBlur = short_ * 0.018;
  const vanishX = w * 0.5;
  for (let i = -9; i <= 9; i++) {
    const xBottom = vanishX + i * (w * 0.09);
    ctx.beginPath();
    ctx.moveTo(vanishX, horizonY);
    ctx.lineTo(xBottom, h);
    ctx.stroke();
  }
  const rows = 8;
  for (let j = 1; j <= rows; j++) {
    const t = j / rows;
    const y = horizonY + (h - horizonY) * t * t;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }
  ctx.restore();

  addVignette(ctx, w, h, 0.32);
  addGrain(ctx, w, h, rng, 0.02);
}

/** Draws one puffy cumulus cloud from many overlapping, irregularly-sized
 *  soft-edged lobes: a blurred ambient shadow for grounding, a
 *  gradient-shaded body (cool grey-blue underside, warm sun-facing side)
 *  for real volume, fine wispy edge detail, and a soft sunlit highlight —
 *  aiming for a believable photographic cloud rather than a cartoon bubble. */
function drawCloudPuff(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  rng: () => number,
  shadowTint: string,
  warmTint: string,
  detail: number,
) {
  const lightAngle = -Math.PI * 0.72; // fixed "sun" direction, upper-left-ish
  const lobes = 8 + Math.floor(rng() * 6);
  const positions: { x: number; y: number; r: number; squash: number }[] = [];
  for (let i = 0; i < lobes; i++) {
    const angle = (i / lobes) * Math.PI * 2 + rng() * 0.45;
    const dist = r * (0.24 + rng() * 0.5);
    const lobeR = r * (0.3 + rng() * 0.5);
    positions.push({
      x: cx + Math.cos(angle) * dist,
      y: cy + Math.sin(angle) * dist * 0.5 - r * 0.08,
      r: lobeR,
      squash: 0.7 + rng() * 0.25,
    });
  }
  positions.push({ x: cx, y: cy, r: r * 0.8, squash: 0.75 });

  const cw = ctx.canvas.width;
  const ch = ctx.canvas.height;

  blurredLayer(ctx, cw, ch, r * 0.22, (sctx) => {
    sctx.fillStyle = withAlpha(shadowTint, 0.2);
    for (const p of positions) {
      sctx.beginPath();
      sctx.ellipse(p.x, p.y + r * 0.2, p.r * 1.05, p.r * p.squash * 0.95, 0, 0, Math.PI * 2);
      sctx.fill();
    }
  }, 720, boxAround(cx, cy, r * 2.4));

  blurredLayer(ctx, cw, ch, Math.max(1, r * 0.05), (sctx) => {
    for (const p of positions) {
      const dx = (p.x - cx) / r;
      const dy = (p.y - cy) / r;
      const lit = clamp(Math.cos(lightAngle) * dx + Math.sin(lightAngle) * dy, -1, 1);
      const grad = sctx.createLinearGradient(p.x, p.y - p.r, p.x, p.y + p.r);
      const top = mixHex("#ffffff", warmTint, 0.18 + Math.max(0, lit) * 0.22);
      const mid = mixHex("#eef2f7", shadowTint, 0.15 + Math.max(0, -lit) * 0.25);
      grad.addColorStop(0, top);
      grad.addColorStop(0.55, mid);
      grad.addColorStop(1, withAlpha(shadowTint, 0.6));
      sctx.fillStyle = grad;
      sctx.beginPath();
      sctx.ellipse(p.x, p.y, p.r, p.r * p.squash, 0, 0, Math.PI * 2);
      sctx.fill();
    }
  }, 720, boxAround(cx, cy, r * 2.2));

  // Fine wispy edge lobes for a less uniformly "bubbly" silhouette.
  if (detail > 0.5) {
    blurredLayer(ctx, cw, ch, Math.max(1, r * 0.06), (sctx) => {
      const wisps = 4 + Math.floor(rng() * 4);
      for (let i = 0; i < wisps; i++) {
        const angle = rng() * Math.PI * 2;
        const dist = r * (0.6 + rng() * 0.5);
        const wr = r * (0.1 + rng() * 0.16);
        const wx = cx + Math.cos(angle) * dist;
        const wy = cy + Math.sin(angle) * dist * 0.5;
        sctx.fillStyle = withAlpha("#ffffff", 0.35 + rng() * 0.3);
        sctx.beginPath();
        sctx.ellipse(wx, wy, wr, wr * 0.55, angle, 0, Math.PI * 2);
        sctx.fill();
      }
    }, 720, boxAround(cx, cy, r * 1.8));
  }

  blurredLayer(ctx, cw, ch, r * 0.13, (sctx) => {
    sctx.fillStyle = withAlpha(mixHex("#ffffff", warmTint, 0.15), 0.65);
    sctx.beginPath();
    sctx.ellipse(cx - r * 0.24, cy - r * 0.34, r * 0.48, r * 0.26, -0.3, 0, Math.PI * 2);
    sctx.fill();
  }, 720, boxAround(cx - r * 0.24, cy - r * 0.34, r * 0.95));
}

/** Draws one wispy, semi-transparent streak of high cirrus cloud — a
 *  stretched, horizontally-blurred smear rather than a puffy cumulus lobe,
 *  which reads as atmospheric haze high in a real sky photo. */
function drawCirrusStreak(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  lenX: number,
  thickness: number,
  rng: () => number,
) {
  const rot = (rng() - 0.5) * 0.2;
  blurredLayer(ctx, ctx.canvas.width, ctx.canvas.height, thickness * 0.7, (sctx) => {
    sctx.translate(cx, cy);
    sctx.rotate(rot);
    const grad = sctx.createLinearGradient(-lenX / 2, 0, lenX / 2, 0);
    grad.addColorStop(0, "rgba(255,255,255,0)");
    grad.addColorStop(0.3, `rgba(255,255,255,${0.25 + rng() * 0.2})`);
    grad.addColorStop(0.7, `rgba(255,255,255,${0.2 + rng() * 0.2})`);
    grad.addColorStop(1, "rgba(255,255,255,0)");
    sctx.fillStyle = grad;
    sctx.beginPath();
    sctx.ellipse(0, 0, lenX / 2, thickness / 2, 0, 0, Math.PI * 2);
    sctx.fill();
  }, 720, boxAround(cx, cy, lenX * 0.65 + thickness * 2));
}

/** Cloudscape — a believable blue sky filled with layered clouds: high,
 *  wispy cirrus haze, a mid layer of small hazy puffs, a soft palette-tinted
 *  sun glow, and large foreground cumulus clouds built from overlapping,
 *  irregular, softly-blurred lobes with subtle grey undertones for real
 *  volume and atmosphere rather than a flat cartoon look. */
function drawCloudscape(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  // Performance: clouds are nothing but soft blurred lobes, so render at a
  // capped internal resolution and upscale (same pattern as Silk Flow) —
  // visually identical, far cheaper than blurring dozens of layers at
  // full export size.
  const MAX_SHORT = 900;
  if (Math.min(w, h) > MAX_SHORT) {
    const scale = MAX_SHORT / Math.min(w, h);
    const ow = Math.round(w * scale), oh = Math.round(h * scale);
    const off = document.createElement("canvas");
    off.width = ow; off.height = oh;
    const octx = off.getContext("2d");
    if (octx) {
      drawCloudscape(octx, ow, oh, colors, seed);
      ctx.save();
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(off, 0, 0, ow, oh, 0, 0, w, h);
      ctx.restore();
      return;
    }
  }
  const rng = mulberry32(seed);
  const short_ = short(w, h);
  const palette = ensurePaletteSize(colors, 3);
  const skyBase = pick(rng, palette);
  const skyDeep = shade(skyBase, -0.22);
  const skyLight = shade(mixHex(skyBase, "#ffffff", 0.55), 0.1);

  // Sky takes its hue from the photo's own palette (not a fixed blue), so
  // sunsets, jewel tones, or pastels from the source photo all read as a
  // believable colored sky rather than a generic daytime blue.
  const sky = ctx.createLinearGradient(0, 0, 0, h);
  sky.addColorStop(0, skyDeep);
  sky.addColorStop(0.45, mixHex(skyDeep, skyBase, 0.5));
  sky.addColorStop(0.8, mixHex(skyBase, skyLight, 0.6));
  sky.addColorStop(1, skyLight);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, h);

  const sunX = w * (0.15 + rng() * 0.7);
  const sunY = h * (0.06 + rng() * 0.16);
  const glow = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, short_ * 0.6);
  glow.addColorStop(0, withAlpha("#fffdf6", 0.5));
  glow.addColorStop(0.4, withAlpha("#fffaf0", 0.18));
  glow.addColorStop(1, withAlpha("#fffaf0", 0));
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, w, h);

  // High wispy cirrus streaks near the top of the frame.
  ctx.save();
  ctx.globalAlpha = 0.35;
  const cirrusCount = 3 + Math.floor(rng() * 3);
  for (let i = 0; i < cirrusCount; i++) {
    const cx = rng() * w;
    const cy = h * (0.02 + rng() * 0.15);
    drawCirrusStreak(ctx, cx, cy, short_ * (0.28 + rng() * 0.24), short_ * (0.02 + rng() * 0.02), rng);
  }
  ctx.restore();

  // Small, hazy mid-distance puffs for depth — each pulls its tint from a
  // different palette color so the whole cloud field carries the photo's colors.
  ctx.save();
  ctx.globalAlpha = 0.4;
  const distantCount = 4 + Math.floor(rng() * 3);
  for (let i = 0; i < distantCount; i++) {
    const cx = rng() * w;
    const cy = h * (0.1 + rng() * 0.2);
    const r = short_ * (0.06 + rng() * 0.05);
    const cloudColor = pick(rng, palette);
    const shadowTint = mixHex(shade(cloudColor, -0.3), "#5c6b7d", 0.4);
    const warmTint = shade(mixHex(cloudColor, "#ffd9a0", 0.35), 0.3);
    drawCloudPuff(ctx, cx, cy, r, rng, shadowTint, warmTint, 0.4);
  }
  ctx.restore();

  // Large foreground cumulus clouds — bigger and packed closer together so
  // neighboring clouds visibly overlap, each carrying its own palette tint.
  const heroCount = 6 + Math.floor(rng() * 4);
  const cols = Math.ceil(Math.sqrt(heroCount));
  for (let i = 0; i < heroCount; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const jitterX = (rng() - 0.5) * (w / cols) * 0.7;
    const jitterY = (rng() - 0.5) * (h * 0.5) * 0.7;
    const cx = w * ((col + 0.5) / cols) + jitterX;
    const cy = h * 0.4 + row * (h * 0.28) + jitterY;
    const r = short_ * (0.16 + rng() * 0.13);
    const cloudColor = pick(rng, palette);
    const shadowTint = mixHex(shade(cloudColor, -0.3), "#5c6b7d", 0.4);
    const warmTint = shade(mixHex(cloudColor, "#ffd9a0", 0.35), 0.3);
    drawCloudPuff(ctx, cx, cy, r, rng, shadowTint, warmTint, 1);
  }

  addGrain(ctx, w, h, rng, 0.012);
}

/** Draws a simplified flat-outline gamepad: rounded body, two grip humps,
 *  a D-pad cross on the left and two round face buttons on the right. */
function drawControllerOutline(ctx: CanvasRenderingContext2D, size: number, color: string, lineWidth: number) {
  const bw = size;
  const bh = size * 0.42;
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  roundRectPath(ctx, -bw / 2, -bh / 2, bw, bh, bh * 0.5);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(-bw * 0.4, bh * 0.32, bh * 0.4, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(bw * 0.4, bh * 0.32, bh * 0.4, 0, Math.PI * 2);
  ctx.stroke();

  const dpx = -bw * 0.24;
  const dpy = 0;
  const dpArm = bh * 0.16;
  const dpThick = bh * 0.13;
  ctx.beginPath();
  ctx.moveTo(dpx - dpThick, dpy - dpArm);
  ctx.lineTo(dpx + dpThick, dpy - dpArm);
  ctx.lineTo(dpx + dpThick, dpy - dpThick);
  ctx.lineTo(dpx + dpArm, dpy - dpThick);
  ctx.lineTo(dpx + dpArm, dpy + dpThick);
  ctx.lineTo(dpx + dpThick, dpy + dpThick);
  ctx.lineTo(dpx + dpThick, dpy + dpArm);
  ctx.lineTo(dpx - dpThick, dpy + dpArm);
  ctx.lineTo(dpx - dpThick, dpy + dpThick);
  ctx.lineTo(dpx - dpArm, dpy + dpThick);
  ctx.lineTo(dpx - dpArm, dpy - dpThick);
  ctx.lineTo(dpx - dpThick, dpy - dpThick);
  ctx.closePath();
  ctx.stroke();

  const bx = bw * 0.26;
  const br = bh * 0.1;
  ctx.beginPath();
  ctx.arc(bx, -bh * 0.14, br, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(bx + br * 2.1, -bh * 0.14, br, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(bx + br * 1.05, -bh * 0.14 - br * 1.4, br, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(bx + br * 1.05, -bh * 0.14 + br * 1.4, br, 0, Math.PI * 2);
  ctx.stroke();
}

/** Draws a simplified flat-outline arcade joystick: a rounded base plate,
 *  an upright stick, and a round ball top. */
function drawJoystickOutline(ctx: CanvasRenderingContext2D, size: number, color: string, lineWidth: number) {
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  const baseW = size * 0.85;
  const baseH = size * 0.22;
  roundRectPath(ctx, -baseW / 2, size * 0.28, baseW, baseH, baseH * 0.4);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(-size * 0.05, size * 0.28);
  ctx.lineTo(size * 0.02, -size * 0.32);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(size * 0.03, -size * 0.42, size * 0.16, 0, Math.PI * 2);
  ctx.stroke();
}

/** Game Symbols — a scatter of hollow controller-button glyphs (cross,
 *  circle, triangle, square), occasional full gamepad and joystick
 *  outlines, plus tiny pixel-square accents over a black arcade backdrop.
 *  Everything is outlined (not filled) and kept mostly upright like real
 *  controller iconography, distinct from Memphis Pop's filled, freely
 *  rotated shape confetti on a pale base. */
function drawGameSymbols(ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) {
  const rng = mulberry32(seed);
  ctx.fillStyle = "#0a0a0f";
  ctx.fillRect(0, 0, w, h);

  const palette = ensurePaletteSize(colors, 4);
  const short_ = short(w, h);
  const glyphCount = Math.max(14, Math.round((w * h) / (short_ * short_ * 0.032)));

  for (let i = 0; i < glyphCount; i++) {
    const roll = rng();
    const x = rng() * w;
    const y = rng() * h;
    ctx.save();
    ctx.translate(x, y);

    if (roll < 0.34) {
      // Full controller or joystick outline — kept smaller than before and
      // weighted toward controllers so the gamepad shape reads as the star.
      const color = pick(rng, palette);
      const size = short_ * (0.07 + rng() * 0.07);
      const rot = (rng() - 0.5) * 0.2;
      ctx.rotate(rot);
      ctx.lineWidth = Math.max(2, size * 0.05);
      if (rng() > 0.28) drawControllerOutline(ctx, size, color, Math.max(2, size * 0.06));
      else drawJoystickOutline(ctx, size, color, Math.max(2, size * 0.08));
    } else {
      const kind = Math.floor(rng() * 4);
      const color = palette[kind % palette.length];
      const size = short_ * (0.04 + rng() * 0.065);
      const rot = pick(rng, [0, Math.PI / 2, Math.PI, (Math.PI * 3) / 2]) + (rng() - 0.5) * 0.25;
      ctx.rotate(rot);
      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(2, size * 0.12);
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      if (kind === 0) {
        const s = size * 0.42;
        ctx.beginPath();
        ctx.moveTo(-s, -s);
        ctx.lineTo(s, s);
        ctx.moveTo(s, -s);
        ctx.lineTo(-s, s);
        ctx.stroke();
      } else if (kind === 1) {
        ctx.beginPath();
        ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
        ctx.stroke();
      } else if (kind === 2) {
        const s = size / 2;
        ctx.beginPath();
        ctx.moveTo(0, -s);
        ctx.lineTo(s * 0.87, s * 0.6);
        ctx.lineTo(-s * 0.87, s * 0.6);
        ctx.closePath();
        ctx.stroke();
      } else {
        const s = size * 0.72;
        ctx.strokeRect(-s / 2, -s / 2, s, s);
      }
    }
    ctx.restore();
  }

  const dotCount = Math.round(glyphCount * 0.8);
  for (let i = 0; i < dotCount; i++) {
    const color = pick(rng, palette);
    const x = rng() * w;
    const y = rng() * h;
    const s = short_ * (0.006 + rng() * 0.01);
    ctx.fillStyle = withAlpha(color, 0.55 + rng() * 0.35);
    ctx.fillRect(x, y, s, s);
  }

  addVignette(ctx, w, h, 0.28);
  addGrain(ctx, w, h, rng, 0.02);
}

export interface BackgroundStyle {
  id: string;
  name: string;
  description: string;
  draw: (ctx: CanvasRenderingContext2D, w: number, h: number, colors: string[], seed: number) => void;
}

export const BACKGROUND_STYLES: BackgroundStyle[] = [
  {
    id: "silk",
    name: "Silk Flow",
    description:
      "Draped ribbons of satin laid side by side, one for every color in your photo — each with deep valley shadows, a smooth fabric body, and a pale sheen along its crest. Elegant, dimensional, and unmistakably silk.",
    draw: drawSilkFlow,
  },
  {
    id: "low-poly",
    name: "Low-Poly",
    description:
      "A faceted grid of jittered triangles, each filled with a color pulled from your photo and lightly shaded facet-by-facet, plus a soft sheen and vignette — the classic geometric wallpaper look, now with real dimension instead of a flat mosaic.",
    draw: drawLowPoly,
  },
  {
    id: "gradient",
    name: "Gradient Flow",
    description:
      "A smooth diagonal gradient through your photo's dominant colors, lifted with a soft corner bloom and a single studio sheen band — simple and versatile for text overlays, with enough depth to not look flat.",
    draw: drawGradientFlow,
  },
  {
    id: "frosted",
    name: "Frosted Glow",
    description:
      "Soft, heavily blurred color orbs drifting over a two-tone neutral base, scattered with tiny glassy highlights, like light diffusing through frosted glass with a touch of sparkle.",
    draw: drawFrostedGlow,
  },
  {
    id: "blobs",
    name: "Organic Blobs",
    description:
      "Layered, rounded amoeba-like shapes in your photo's colors, each lifted with a soft drop shadow and a glossy inner highlight — a playful, modern blob-art composition that reads as embossed rather than flat.",
    draw: drawOrganicBlobs,
  },
  {
    id: "watercolor",
    name: "Watercolor",
    description:
      "Two layered pigment washes — a broad soft base and a multiply-blended second pass where colors pool and darken at their edges — plus a fine paper grain, as if your photo's palette had bled and re-wet across a page.",
    draw: drawWatercolor,
  },
  {
    id: "splatter",
    name: "Paint Splatter",
    description:
      "Scattered paint drops and drips of varying size, some trailing a tapering tail, with glossy highlight arcs on the larger drops — an energetic, tactile finish for anything that needs a bit of motion.",
    draw: drawSplatter,
  },
  {
    id: "mesh",
    name: "Mesh Gradient",
    description:
      "A field of large, heavily blurred color blobs scattered across a jittered grid so they melt into one continuous, multi-directional wash — the modern \"mesh gradient\" look, with no single sweep direction like Gradient Flow.",
    draw: drawMeshGradient,
  },
  {
    id: "grainy",
    name: "Grainy Gradient",
    description:
      "A calm, matte 3-stop radial blend with heavy monochrome film grain as the main texture instead of an accent — no bloom, no sheen, the most minimal and restrained style in the set.",
    draw: drawGrainyGradient,
  },
  {
    id: "metaball",
    name: "Liquid Metaball",
    description:
      "Soft circles fused into molten, merging blob shapes using a blur-then-contrast trick, cut from a colorful gradient over a darker backdrop — a lava-lamp, mercury-like flow distinct from the separately outlined Organic Blobs.",
    draw: drawLiquidMetaball,
  },
  {
    id: "holo",
    name: "Holographic Chrome",
    description:
      "A tight, repeating rainbow-shift gradient through your photo's colors like light diffracting off foil, with fine diagonal striping and bright specular streaks — high-shine and metallic rather than soft.",
    draw: drawHolographic,
  },
  {
    id: "aurora",
    name: "Aurora Glow",
    description:
      "Wide, wavering ribbons of translucent color drifting across a near-black night sky with a scatter of stars — sweeping horizontal bands rather than round blobs, like the northern lights.",
    draw: drawAuroraGlow,
  },
  {
    id: "duotone",
    name: "Neon Gradient",
    description:
      "Your photo's palette colors remapped pixel-by-pixel through a luminance gradient — dark tones become the deepest hue, midtones shift to a vivid accent, highlights bloom into the lightest color. Organic swirl distortion, CRT scanlines, and a chromatic aberration ghost finish the look.",
    draw: drawDuotone,
  },
  {
    id: "halftone",
    name: "Halftone Dots",
    description:
      "A classic print dot grid whose dot size grows toward a bright focal point, cycling through your photo's colors cell by cell over a near-black base — the only style built from small repeating marks instead of large fields.",
    draw: drawHalftone,
  },
  {
    id: "glass",
    name: "Glassmorphism",
    description:
      "Frosted, semi-transparent glass panels — each with its own edge highlight and soft shadow — floating over a hazy, colorful blurred glow. The only style built as distinct floating panels rather than one continuous field.",
    draw: drawGlassmorphism,
  },
  {
    id: "memphis",
    name: "Memphis Pop",
    description:
      "A scatter of flat, bright geometric shapes — circles, triangles, zigzags, crosses, dashed rings — at random rotations over a pale tinted base, 80s-postmodern style. No gradients or blur: every shape is a flat vector cutout.",
    draw: drawMemphisPop,
  },
  {
    id: "synthwave",
    name: "Synthwave Grid",
    description:
      "A retro sunset sky, a glowing horizon sun with cut horizontal bands, and a neon perspective grid receding to a vanishing point — the only style built as an actual horizon scene instead of an abstract field.",
    draw: drawSynthwave,
  },
  {
    id: "clouds",
    name: "Cloudscape",
    description:
      "A believable blue sky with layered, puffy cumulus clouds — a hazy distant layer, a soft palette-tinted sun glow, and large foreground clouds shaded for real volume. The only style built as a literal photographic sky.",
    draw: drawCloudscape,
  },
  {
    id: "game-symbols",
    name: "Game Symbols",
    description:
      "Hollow controller-button glyphs — cross, circle, triangle, square — plus tiny pixel-square accents scattered over a black arcade backdrop, kept upright like real controller icons rather than freely rotated like Memphis Pop.",
    draw: drawGameSymbols,
  },
];

/** Ensures there are always at least a few usable colors even if the photo
 *  yielded very few distinct dominant colors. */
export function ensurePaletteSize(colors: string[], min = 4): string[] {
  if (colors.length >= min) return colors;
  const padded = [...colors];
  const fallback = ["#FF3C00", "#2B2B52", "#F4C95D", "#4E9F3D"];
  let i = 0;
  while (padded.length < min) {
    padded.push(fallback[i % fallback.length]);
    i++;
  }
  return padded;
}
